import { useState, useEffect, useMemo, useCallback } from "react";
import {
  addDays,
  setHours,
  setMinutes,
  subDays,
  subMonths,
  addMonths,
  addWeeks,
  parseISO,
  format,
  addHours,
  startOfMonth,
  endOfMonth,
  subDays as subDaysFromDate,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
} from "date-fns";
import {
  Calendar as FullCalendar,
  type CalendarEvent,
  type CalendarViewType,
} from "@/components/ui/full-calendar/calendar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { MoreVertical, Search, FilterIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import BookingRequestDetails from "../modal/booking-details";
import type { FacilityBooking } from "@/types/facility/booking";
import {
  useGetAllFacilityBookingsQuery,
  useGetFacilitiesQuery,
} from "@/services/facilityApis";
import useTableData from "@/components/ui/table/data-table-logic";
import TableFilters from "@/components/ui/table/data-table-filter";
import usePagination from "@/hooks/use-pagination";
import useDocumentTitle from "@/components/ui/common/document-title";
import { useDebounce } from "@/hooks/use-debounce";
import { toast } from "@/components/ui/use-toast";
import { ErrorType } from "@/types";
import { useDeleteFacilityBookingMutation } from "@/services/facilityApis";

// Define search criteria interface
interface SearchCriteriaType {
  limit: number; // Made required instead of optional
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  type: string;
  view_type?: string;
  status?: string;
  facility_id?: number[];
  start_date?: string;
  end_date?: string;
  [key: string]: string | number | (string | number)[] | null | undefined;
}

// Calendar view initial criteria (date range based)
const calendarInitialValue: SearchCriteriaType = {
  limit: 1000, // Large limit for calendar view to get all events in range
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "facility_booking",
};

// Grid view initial criteria (paginated like table)
const gridInitialValue: SearchCriteriaType = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "facility_booking",
};

type Facility = {
  id: number;
  name: string;
  color: string;
  selected: boolean;
};

// Transform API booking data to calendar events
const transformBookingToEvent = (booking: FacilityBooking): CalendarEvent => {
  // Handle different date formats more robustly
  let bookingDate: Date;
  try {
    if (booking.booking_date) {
      bookingDate =
        booking.booking_date instanceof Date
          ? booking.booking_date
          : new Date(booking.booking_date);

      // Validate the date
      if (isNaN(bookingDate.getTime())) {
        console.warn(
          "Invalid booking date, using current date:",
          booking.booking_date
        );
        bookingDate = new Date();
      }
    } else {
      console.warn("No booking date found, using current date");
      bookingDate = new Date();
    }
  } catch (error) {
    console.warn("Error parsing booking date:", booking.booking_date, error);
    bookingDate = new Date();
  }

  const startTime = booking.start_time
    ? parseISO(`${format(bookingDate, "yyyy-MM-dd")}T${booking.start_time}`)
    : bookingDate;

  const endTime = booking.end_time
    ? parseISO(`${format(bookingDate, "yyyy-MM-dd")}T${booking.end_time}`)
    : addHours(startTime, 1);

  // Extract base color name from booking.color (e.g., "purple-700" -> "purple")
  const extractBaseColor = (colorString?: string): string | undefined => {
    if (!colorString) return undefined;
    // Split by hyphen and take the first part (base color name)
    const parts = colorString.split('-');
    return parts[0] || undefined;
  };

  const transformedEvent = {
    id: booking.id?.toString() || Math.random().toString(),
    title: booking.facility_name || "Facility Booking",
    start: startTime,
    end: endTime,
    color: extractBaseColor(booking.color), // Extract and add color from booking
    tags: [
      {
        label: booking.status?.toUpperCase() || "BOOKED",
        color: getStatusColor(booking.status),
      },
      {
        label: booking.payment_status?.toUpperCase() || "UNPAID",
        color: getPaymentColor(booking.payment_status),
      },
    ],
    description: `Booking for ${booking.facility_name}`,
    location: booking.facility_name || "Unknown Facility",
    // Pass the full booking data for the modal
    originalData: booking,
  } as CalendarEvent;

  return transformedEvent;
};

// Get color based on booking status
const getStatusColor = (status?: string): string => {
  switch (status?.toLowerCase()) {
    case "approved":
      return "green";
    case "pending":
      return "yellow";
    case "rejected":
      return "red";
    case "confirmed":
      return "blue";
    default:
      return "gray";
  }
};

// Get color based on payment status
const getPaymentColor = (paymentStatus?: string): string => {
  switch (paymentStatus?.toLowerCase()) {
    case "paid":
      return "green";
    case "pending":
      return "orange";
    case "failed":
      return "red";
    default:
      return "gray";
  }
};

export default function FacilityBooking() {
  useDocumentTitle("Bookings");
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  // Get access level for facility bookings
  const facilityBookingAccess = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .facility_bookings ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  // State management
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null
  );
  const [isViewEventOpen, setIsViewEventOpen] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<FacilityBooking | undefined>(
    undefined
  );
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});
  const [facilitySearch, setFacilitySearch] = useState("");
  const [viewMode, setViewMode] = useState<"calendar" | "grid">("calendar");
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [calendarViewType, setCalendarViewType] =
    useState<CalendarViewType>("month"); // New state for calendar view type
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined); // State for selected date filter
  // Add state to track the calendar's visible month
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());

  // Determine if any filter is applied to the data shown in calendar/grid
  const isFilterApplied = useMemo(() => {
    // Advanced filters: any non-empty value in filterData
    const hasAdvancedFilters = Object.values(filterData || {}).some((value) => {
      if (value === null || value === undefined) return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "string") return value.trim() !== "";
      return true;
    });

    // Date filter
    const hasDateFilter = !!selectedDate;

    // Facility selection filter (not all selected)
    const hasFacilityFilter = facilities.length > 0 && facilities.some((f) => !f.selected);

    return hasAdvancedFilters || hasDateFilter || hasFacilityFilter;
  }, [filterData, selectedDate, facilities]);

  // Debounced facility search value
  const debouncedFacilitySearch = useDebounce(facilitySearch, 500);

  // Separate search criteria for calendar and grid views
  const [calendarSearchCriteria, setCalendarSearchCriteria] =
    useState<SearchCriteriaType>(calendarInitialValue);
  const [gridSearchCriteria, setGridSearchCriteria] =
    useState<SearchCriteriaType>(gridInitialValue);

  // Use appropriate search criteria based on view mode
  const currentSearchCriteria =
    viewMode === "calendar" ? calendarSearchCriteria : gridSearchCriteria;
  const setCurrentSearchCriteria =
    viewMode === "calendar" ? setCalendarSearchCriteria : setGridSearchCriteria;

  // Table data hook for search/filter functionality (using dummy initial value)
  const { toggleSortOrder, inputValue, setInputValue } =
    useTableData(gridInitialValue);

  // Generate query string from current search criteria
  const generateQueryString = (criteria: SearchCriteriaType) => {
    const params = new URLSearchParams();
    Object.entries(criteria).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) {
          value.forEach((v) => params.append(key, v.toString()));
        } else {
          params.append(key, value.toString());
        }
      }
    });

    // Add facility filter based on selected facilities
    const selectedFacilityIds = facilities
      .filter((f) => f.selected)
      .map((f) => f.id);

    if (selectedFacilityIds.length > 0) {
      selectedFacilityIds.forEach((id) => {
        params.append("facility_ids", id.toString());
      });
    }

    // Add selected date filter if a date is selected
    if (selectedDate) {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      params.append("start_date", dateStr);
      params.append("end_date", dateStr);
    }

    return params.toString();
  };

  const currentQuery = generateQueryString(currentSearchCriteria);

  // API calls
  const {
    data: bookingsData,
    isLoading: isLoadingBookings,
    refetch: refetchBookings,
  } = useGetAllFacilityBookingsQuery(
    {
      query: currentQuery + "&view_type=admin",
      orgId: orgId,
    },
    {
      skip: !orgId || !currentQuery,
    }
  );

  const [deleteBooking, { isLoading: deleteLoading }] =
    useDeleteFacilityBookingMutation();

  // Pagination hook for grid view (similar to table component)
  // Note: The API returns 'filtered_count' (singular) but the type expects 'filtered_counts' (plural)
  // This is a known mismatch between the API and type definitions
  // Use total_counts as fallback if filtered_counts is 0 but data exists
  const totalRecords = bookingsData?.filtered_counts || 0;

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria: gridSearchCriteria,
    setSearchCriteria: setGridSearchCriteria,
  });

  // Generate facility search query
  const facilityQuery = useMemo(() => {
    const params = new URLSearchParams();
    params.append("sort_key", "id");
    params.append("sort_order", "desc");
    params.append("bookable", "true");
    if (debouncedFacilitySearch.trim()) {
      params.append("search_key", debouncedFacilitySearch.trim());
    }
    return params.toString();
  }, [debouncedFacilitySearch]);

  const { data: facilitiesData, isLoading: isLoadingFacilities } =
    useGetFacilitiesQuery(
      {
        query: facilityQuery,
        orgId: orgId,
      },
      {
        skip: !orgId,
      }
    );

  // Transform API data to calendar events
  const events = useMemo(() => {
    if (!bookingsData?.data) {
      console.log("No bookings data available");
      return [];
    }

    // For grid view, the API should already return paginated data
    // For calendar view, we get all events in the date range
    const transformedEvents = bookingsData.data.map((booking, index) => {
      if (index === 0) {
        console.log("Sample booking being transformed:", booking);
      }
      return transformBookingToEvent(booking);
    });

    return transformedEvents;
  }, [bookingsData?.data, viewMode]);

  // Initialize facilities from API data
  useEffect(() => {
    if (facilitiesData?.data) {
      const facilityColors = [
        "border-green-500",
        "border-orange-500",
        "border-yellow-500",
        "border-amber-700",
        "border-purple-500",
        "border-blue-500",
        "border-red-500",
        "border-pink-500",
      ];

      const transformedFacilities = facilitiesData.data.map(
        (facility: any, index: number) => ({
          id: facility.id,
          name: facility.name,
          color: facilityColors[index % facilityColors.length],
          selected: true, // Default to all selected
        })
      );

      setFacilities(transformedFacilities);
    }
  }, [facilitiesData?.data]);

  // Refetch bookings when facility selection changes
  useEffect(() => {
    if (facilities.length > 0) {
      refetchBookings();
    }
  }, [facilities, refetchBookings]);

  // Refetch bookings when selected date changes
  useEffect(() => {
    refetchBookings();
  }, [selectedDate, refetchBookings]);

  // Initialize calendar with current month by default
  useEffect(() => {
    // Set to start and end of the current month by default
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);

    setCalendarSearchCriteria((prev) => ({
      ...prev,
      start_date: format(monthStart, "yyyy-MM-dd"),
      end_date: format(monthEnd, "yyyy-MM-dd"),
    }));
  }, [currentDate]); // Run when currentDate changes to update month range

  const handleCloseDailog = () => setIsDialogOpen(false);

  const handleCreateEvent = () => {
    setIsDialogOpen(true);
    setFormData(undefined);
  };

  const handleViewEvent = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setIsViewEventOpen(true);
  };

  const handleEditEvent = (event: CalendarEvent) => {
    const bookingData = event.originalData as FacilityBooking;
    if (bookingData) {
      setIsDialogOpen(true);
      setAction("edit");
      setFormData(bookingData);
    }
  };

  const handleDeleteEvent = async (event: CalendarEvent) => {
    // The delete functionality is handled within the CalendarEventActions component
    // This handler is just for consistency with the interface
    const bookingData = event.originalData as FacilityBooking;

    try {
      const resp = await deleteBooking(bookingData.id as number).unwrap();
      if (resp) {
        if (bookingsData?.filtered_counts == 1 && viewMode === "grid"&& gridSearchCriteria.offset > 0) {
          setGridSearchCriteria((prev) => ({
            ...prev,
            offset: prev.offset-1,
          }));
          setFilterData((prev) => ({ ...prev, offset: prev.offset-1 }));
        }
        setInputValue("");
        refetchBookings();
        toast({
          variant: "success",
          title: "Facility Deleted Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${
            typedError.data?.detail ||
            (typedError.data as { message?: string }).message
          }`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const handleFilterEvents = () => {
    setOpenFilter(true);
  };

  // Handle date selection from the calendar (existing)
  const handleDateSelect = (date: Date | undefined) => {
    if (date && selectedDate && format(date, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd")) {
      // If the same date is clicked, unselect it
      setSelectedDate(undefined);
    } else if (date) {
      // Select the new date
      setSelectedDate(date);
      
      // Update currentDate to show the selected date in the calendar view
      setCurrentDate(date);
      
      // Update calendar month to keep the date picker in sync (but don't change view type)
      setCalendarMonth(startOfMonth(date));
      
      // Update calendar search criteria based on current view type
      // This ensures week/day views show the selected date
      if (viewMode === "calendar") {
        let newStartDate: Date;
        let newEndDate: Date;

        if (calendarViewType === "month") {
          // For month view, show the month containing the selected date
          newStartDate = startOfMonth(date);
          newEndDate = endOfMonth(date);
        } else if (calendarViewType === "week") {
          // For week view, show the week containing the selected date
          newStartDate = startOfWeek(date, { weekStartsOn: 1 });
          newEndDate = endOfWeek(date, { weekStartsOn: 1 });
        } else {
          // For day view, show the specific selected day
          newStartDate = startOfDay(date);
          newEndDate = endOfDay(date);
        }

        setCalendarSearchCriteria((prev) => ({
          ...prev,
          start_date: format(newStartDate, "yyyy-MM-dd"),
          end_date: format(newEndDate, "yyyy-MM-dd"),
          offset: 0,
        }));
      }
    }
  };

  // NEW: Handle month navigation in the Calendar component
  const handleCalendarMonthChange = (month: Date) => {
    console.log("Calendar month changed to:", month, "Current view:", calendarViewType, "View mode:", viewMode);
    setCalendarMonth(month);
    
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    
    // Update the currentDate to match the calendar's visible month
    setCurrentDate(month);
    
    // If user is in grid view or week/day view, switch to month view and calendar mode
    if (viewMode === "grid") {
      console.log("Switching from grid view to calendar month view");
      setViewMode("calendar");
      setCalendarViewType("month");
    } else if (viewMode === "calendar" && (calendarViewType === "week" || calendarViewType === "day")) {
      console.log(`Switching from ${calendarViewType} view to month view`);
      setCalendarViewType("month");
    }
    
    // Update search criteria with the new month range
    setCalendarSearchCriteria((prev) => ({
      ...prev,
      start_date: format(monthStart, "yyyy-MM-dd"),
      end_date: format(monthEnd, "yyyy-MM-dd"),
      offset: 0,
    }));
  };

  const toggleFacilitySelection = (id: number) => {
    setFacilities((prev) =>
      prev.map((facility) =>
        facility.id === id
          ? { ...facility, selected: !facility.selected }
          : facility
      )
    );
  };

  // Filter facilities based on search
  const filteredFacilities = useMemo(() => {
    return facilities.filter((facility) =>
      facility.name.toLowerCase().includes(facilitySearch.toLowerCase())
    );
  }, [facilities, facilitySearch]);

  // Navigation handlers for calendar pagination
  const handlePrevious = useCallback(() => {
    console.log("Bookings: Previous clicked for view:", calendarViewType);
    setCurrentDate((prev) => {
      let newDate: Date;

      if (calendarViewType === "month") {
        newDate = subMonths(prev, 1);
        setCalendarMonth(newDate); // ADD THIS LINE
      } else if (calendarViewType === "week") {
        // For week view, go back by 1 week
        newDate = addDays(prev, -7);
      } else {
        // For day view, go back by 1 day
        newDate = addDays(prev, -1);
      }

      console.log(
        "Bookings: Previous navigation - from:",
        prev,
        "to:",
        newDate,
        "view:",
        calendarViewType
      );
      return newDate;
    });
  }, [calendarViewType]);

  const handleNext = useCallback(() => {
    console.log("Bookings: Next clicked for view:", calendarViewType);
    setCurrentDate((prev) => {
      let newDate: Date;

      if (calendarViewType === "month") {
        newDate = addMonths(prev, 1);
        setCalendarMonth(newDate); // ADD THIS LINE
      } else if (calendarViewType === "week") {
        // For week view, go forward by 1 week
        newDate = addDays(prev, 7);
      } else {
        // For day view, go forward by 1 day
        newDate = addDays(prev, 1);
      }

      console.log(
        "Bookings: Next navigation - from:",
        prev,
        "to:",
        newDate,
        "view:",
        calendarViewType
      );
      return newDate;
    });
  }, [calendarViewType]);

  // Handler for changing calendar view (month, week, day)
  const handleCalendarViewChange = useCallback((newView: CalendarViewType) => {
    console.log("Calendar view changing to:", newView);
    setCalendarViewType(newView);

    // Update currentDate and date range based on new view
    // Use selectedDate if available, otherwise use currentDate
    setCurrentDate((prev) => {
      // Prioritize selectedDate if it exists, otherwise use prev (currentDate)
      const dateToUse = selectedDate || prev;
      
      console.log(
        "Calculating date range for new view:",
        newView,
        "with date:",
        dateToUse,
        "selectedDate:",
        selectedDate
      );

      let newStartDate: Date;
      let newEndDate: Date;

      if (newView === "month") {
        newStartDate = startOfMonth(dateToUse);
        newEndDate = endOfMonth(dateToUse);
      } else if (newView === "week") {
        newStartDate = startOfWeek(dateToUse, { weekStartsOn: 1 });
        newEndDate = endOfWeek(dateToUse, { weekStartsOn: 1 });
      } else {
        // Day view
        newStartDate = startOfDay(dateToUse);
        newEndDate = endOfDay(dateToUse);
      }

      console.log("New date range:", {
        start: format(newStartDate, "yyyy-MM-dd"),
        end: format(newEndDate, "yyyy-MM-dd"),
      });

      setCalendarSearchCriteria((prevCriteria) => ({
        ...prevCriteria,
        start_date: format(newStartDate, "yyyy-MM-dd"),
        end_date: format(newEndDate, "yyyy-MM-dd"),
        offset: 0, // Reset to first page when view changes
      }));

      // Return the date to use for the new view context
      return new Date(dateToUse); // Create a new Date instance to trigger re-render
    });
  }, [selectedDate]);

  // Date range change handler for calendar view
  const handleDateRangeChange = useCallback(
    (startDate: Date, endDate: Date) => {
      console.log("handleDateRangeChange called:", {
        startDate: format(startDate, "yyyy-MM-dd"),
        endDate: format(endDate, "yyyy-MM-dd"),
        viewMode,
        calendarViewType,
      });

      // Only update if we're in calendar mode to avoid conflicts
      if (viewMode === "calendar") {
        const formattedStartDate = format(startDate, "yyyy-MM-dd");
        const formattedEndDate = format(endDate, "yyyy-MM-dd");

        setCalendarSearchCriteria((prev) => {
          // Only update if the dates are actually different
          if (
            prev.start_date !== formattedStartDate ||
            prev.end_date !== formattedEndDate
          ) {
            console.log(
              "Updating calendar search criteria with new date range"
            );
            return {
              ...prev,
              start_date: formattedStartDate,
              end_date: formattedEndDate,
              offset: 0, // Reset to first page when date changes
            };
          }
          console.log("Date range unchanged, skipping update");
          return prev;
        });
      } else {
        console.log("Not in calendar mode, skipping date range update");
      }
    },
    [viewMode, calendarViewType]
  );

  // View mode change handler
  const handleViewModeChange = (newViewMode: "calendar" | "grid") => {
    console.log("Switching view mode to:", newViewMode);
    setViewMode(newViewMode);

    // Clear selected date when switching views to avoid confusion
    setSelectedDate(undefined);

    // Reset and clean up search criteria when switching views
    if (newViewMode === "grid") {
      // Reset grid search criteria to default values without date filters
      const newGridCriteria = {
        ...calendarSearchCriteria,
        ...gridSearchCriteria,
        limit: 10,
        offset: 0,
        sort_order: "desc",
        sort_key: "id",
        type: "facility_booking",
      };
      setGridSearchCriteria(newGridCriteria);
    } else if (newViewMode === "calendar") {
      // Reset current date to today when switching to calendar
      const now = new Date();
      setCurrentDate(now);

      // Ensure calendar has proper date range
      // Recalculate based on current calendarViewType
      let newStartDate: Date;
      let newEndDate: Date;

      if (calendarViewType === "month") {
        newStartDate = startOfMonth(now);
        newEndDate = endOfMonth(now);
      } else if (calendarViewType === "week") {
        newStartDate = startOfWeek(now, { weekStartsOn: 1 });
        newEndDate = endOfWeek(now, { weekStartsOn: 1 });
      } else {
        // Day view
        newStartDate = startOfDay(now);
        newEndDate = endOfDay(now);
      }

      setCalendarSearchCriteria((prev) => ({
        ...gridSearchCriteria,
        ...calendarSearchCriteria,
        start_date: format(newStartDate, "yyyy-MM-dd"),
        end_date: format(newEndDate, "yyyy-MM-dd"),
        offset: 0,
      }));
    }
  };

  // Filter data for advanced filtering
  const handleFilterChange = (
    field: string,
    value: string | number | number[]
  ) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Filter configuration for advanced filtering
  const filterDisplay = [
    {
      type: "select",
      name: "status_id",
      label: "Booking Status",
      options: [
        { id: "2", name: "Pending" },
        { id: "6", name: "Rejected" },
        { id: "7", name: "Approved" },
      ],
      function: (value: string) => handleFilterChange("status_id", value),
    },
    {
      type: "select",
      name: "payment_status_id",
      label: "Payment Status",
      options: [
        { id: "1", name: "Paid" },
        { id: "2", name: "Pending" },
      ],
      function: (value: string) => handleFilterChange("payment_status_id", value),
    },
  ];

  return (
    <>
      <div className="flex flex-col md:flex-row py-5 px-2 justify-between gap-4">
        <div className="min-w-80 bg-white p-3 space-y-3">
          <div className="relative w-fit mx-auto">
            <Calendar
              mode="single"
              fromYear={2025}
              toYear={2050}
              defaultMonth={new Date(Date.now())}
              month={calendarMonth}
              onMonthChange={handleCalendarMonthChange}
              selected={selectedDate}
              onSelect={handleDateSelect}
              initialFocus
              className="py-0 "
            />
          </div>

          {/* Selected date indicator */}
          {selectedDate && (
            <div className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded-md">
              <span className="text-sm text-blue-700">
                Filtering by: {format(selectedDate, "MMM dd, yyyy")}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedDate(undefined)}
                className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800"
              >
                ×
              </Button>
            </div>
          )}

          {/* filter for facility */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-medium text-lg">Search Facility</h2>
              <span className="text-sm text-gray-500">
                {facilities.filter((f) => f.selected).length} of{" "}
                {facilities.length} selected
              </span>
            </div>
            <div className="relative mb-6">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                placeholder="Search by Name"
                value={facilitySearch}
                onChange={(e) => setFacilitySearch(e.target.value)}
                className={cn(
                  "pl-10 border-muted border-2 rounded-md focus-visible:ring-purple-500",
                  (isLoadingFacilities ||
                    facilitySearch !== debouncedFacilitySearch) &&
                    "pr-12"
                )}
                disabled={isLoadingFacilities}
              />
            </div>

            <div className="space-y-3 max-h-80 pr-2 custom-scrollbar">
              {isLoadingFacilities ? (
                <div className="flex justify-center items-center p-4">
                  <div className="flex space-x-2">
                    <div className="size-3 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                    <div className="size-3 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                    <div className="size-3 bg-gray-400 rounded-full animate-bounce"></div>
                  </div>
                </div>
              ) : filteredFacilities.length === 0 ? (
                <div className="text-center text-gray-500 p-4">
                  {facilities.length === 0
                    ? "No facilities found"
                    : debouncedFacilitySearch.trim()
                    ? `No facilities match "${debouncedFacilitySearch}"`
                    : "No facilities available"}
                </div>
              ) : (
                filteredFacilities.map((facility) => (
                  <div
                    key={facility.id}
                    className={cn(
                      "border-2 rounded-md p-2 flex items-center justify-between",
                      facility.color
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={`facility-${facility.id}`}
                        checked={facility.selected}
                        onCheckedChange={() =>
                          toggleFacilitySelection(facility.id)
                        }
                        className={cn(
                          "h-5 w-5 rounded-sm border-2",
                          facility.selected &&
                            facility.color
                              .replace("border", "bg")
                              .replace("border", "text-white")
                        )}
                      />
                      <label
                        htmlFor={`facility-${facility.id}`}
                        className="font-medium cursor-pointer break-all"
                      >
                        {facility.name}
                      </label>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Facility Selection Controls */}
            <div className="flex gap-2 pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFacilities((prev) =>
                    prev.map((f) => ({ ...f, selected: true }))
                  );
                  // Refetch bookings after selecting all facilities
                  setTimeout(() => refetchBookings(), 100);
                }}
                className="text-xs flex-1"
              >
                Select All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFacilities((prev) =>
                    prev.map((f) => ({ ...f, selected: false }))
                  );
                  // Refetch bookings after clearing all facilities
                  setTimeout(() => refetchBookings(), 100);
                }}
                className="text-xs flex-1"
              >
                Clear All
              </Button>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="relative w-full rounded-lg bg-white">
          <>
            {isLoadingBookings ? (
              <div className="flex justify-center items-center h-96">
                <div className="flex space-x-2">
                  <div className="size-4 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="size-4 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="size-4 bg-primary rounded-full animate-bounce"></div>
                </div>
              </div>
            ) : (
              <FullCalendar
                events={events}
                initialView={calendarViewType} // Pass current calendar view type
                initialDate={currentDate}
                key={`calendar-${currentDate.getTime()}-${calendarViewType}`} // Force re-render when date/view changes
                onViewEvent={handleViewEvent}
                onFilterEvents={handleFilterEvents}
                isFilterApplied={isFilterApplied}
                onDateRangeChange={handleDateRangeChange}
                onViewModeChange={handleViewModeChange}
                onCalendarViewChange={handleCalendarViewChange} // Pass new prop
                onPrevious={handlePrevious}
                onNext={handleNext}
                refetch={refetchBookings}
                access={facilityBookingAccess}
                gridViewProps={{
                  totalItems: totalRecords,
                  currentPage: gridSearchCriteria.offset,
                  itemsPerPage: gridSearchCriteria.limit,
                  isLoading: isLoadingBookings,
                  onLimitChange: handleLimitChange,
                  onNextPage: handleNextPage,
                  onPrevPage: handlePrevPage,
                  onFirstPage: handleFirstPage,
                  onLastPage: handleLastPage,
                  isLastPage: isLastPage,
                }}
              />
            )}
          </>
        </div>
      </div>
      

      <BookingRequestDetails
        isOpen={isViewEventOpen}
        onClose={() => setIsViewEventOpen(false)}
        data={(selectedEvent as any)?.originalData || selectedEvent}
        refetch={refetchBookings}
      />

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={
          viewMode === "calendar" ? calendarInitialValue : gridInitialValue
        }
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setCurrentSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />
    </>
  );
}
